import './App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import ProductPage from './pages/ProductPage';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { CatalogueProvider } from './context/CatalogueContext';
import { Toaster } from './components/ui/toaster';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import FloatingActions from './components/FloatingActions';
import WhatsAppWidget from './components/WhatsAppWidget';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const ShippingPolicy = lazy(() => import('./pages/legal/ShippingPolicy'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const ContactPage = lazy(() => import('./pages/legal/ContactPage'));

function AppRoutes() {
  const location = useLocation();
  const onProduct = location.pathname.startsWith('/products/');
  const onHome = location.pathname === '/';
  // Keep Home mounted while browsing products so Back restores scroll
  const keepHome = onHome || onProduct;

  return (
    <>
      <ScrollToTop />
      {keepHome && (
        <div style={{ display: onHome ? 'block' : 'none' }} aria-hidden={onProduct || undefined}>
          <Home />
        </div>
      )}
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={null} />
          <Route path="/products/:slug" element={<ProductPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/thank-you/:orderId" element={<ThankYouPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path="/account/*"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute admin>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
      <FloatingActions />
      <WhatsAppWidget />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CatalogueProvider>
          <CartProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
            <Toaster />
          </CartProvider>
        </CatalogueProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
